import {
  type Permission,
  type Session,
  sessionSchema,
} from '@arquivo-morto/contracts';
import { Button } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { type ReactNode, useCallback } from 'react';
import { ApiError } from '#src/api/http-client';
import { useApi, useAuth } from '#src/auth/AuthProvider';
import { StatusScreen } from '#src/shared/StatusScreen';

const SESSION_KEY = ['session'] as const;
const FORBIDDEN = 403;

function useSessionQuery() {
  const api = useApi();
  return useQuery({
    queryKey: SESSION_KEY,
    queryFn: () => api.get('/auth/me', sessionSchema),
  });
}

/**
 * Só renderiza a aplicação depois de saber quem entrou. Sem isto, um link
 * direto mostraria "sem acesso" antes de as permissões chegarem.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const session = useSessionQuery();
  const { signOut } = useAuth();

  if (session.isPending) {
    return <StatusScreen fullPage loading title="Entrando..." />;
  }
  if (session.isError) {
    const blocked =
      session.error instanceof ApiError && session.error.status === FORBIDDEN;
    return (
      <StatusScreen
        fullPage
        tone={blocked ? 'denied' : 'error'}
        title={blocked ? 'Acesso bloqueado' : 'Não foi possível entrar'}
        description={session.error.message}
        action={
          <>
            <Button onClick={() => session.refetch()}>Tentar de novo</Button>
            {signOut && (
              <Button onClick={signOut}>Entrar com outro e-mail</Button>
            )}
          </>
        }
      />
    );
  }
  return children;
}

/** Sessão de quem entrou. Só dentro do `SessionGate`, onde ela já existe. */
export function useSession(): Session {
  const { data } = useSessionQuery();
  if (data === undefined) {
    throw new Error('useSession fora do SessionGate');
  }
  return data;
}

/**
 * Verificador de acesso. Sem permissão exigida, basta estar autenticado. A
 * decisão é do backend (as permissões vêm de `/auth/me`); aqui só se esconde
 * o que não seria liberado de qualquer forma.
 */
export function useAccess(): (permission: Permission | undefined) => boolean {
  const { permissions } = useSession();
  return useCallback(
    (permission) =>
      permission === undefined || permissions.includes(permission),
    [permissions],
  );
}
